import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { AffiliateProvider } from "./affiliate/affiliateContext";
// MSAL imports
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  IPublicClientApplication,
} from "@azure/msal-browser";
import { loginRequest } from "./authConfig";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Provider } from "react-redux";
import { persistor, store } from "./store/store";
import { clearAccessToken } from "./store/modules/auth/authSlice";
import { PersistGate } from "redux-persist/integration/react";
import { createMsalInstance } from "./auth/msalInstance";
import { resolveAffiliateOnce } from "./affiliate/affiliateContext";
import { getAffiliateSlug } from "./affiliate/affiliateService";
import { applyAuthResult, notifySessionChanged } from "./auth/session";
import {
  consumeReturnTo,
  saveLoginHint,
  saveRedirectError,
} from "./auth/browserStorage";
import { resetRedirectLoopGuard } from "./auth/redirectFallback";

export let msalInstance: IPublicClientApplication;

const LOGGED_OUT_PATH = "/logged-out";

function registerAuthEvents(pca: IPublicClientApplication) {
  pca.addEventCallback((event: EventMessage) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
    ) {
      const payload = event.payload as AuthenticationResult | null;
      if (payload?.accessToken) {
        applyAuthResult(pca, payload);
      }
    } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
      // Storage is cleared and navigation handled by logOut(); MSAL can also raise this internally.
      store.dispatch(clearAccessToken());
      notifySessionChanged();
    }
  });
}

async function restoreSession(pca: IPublicClientApplication) {
  try {
    const result = await pca.handleRedirectPromise();
    if (result) {
      resetRedirectLoopGuard();
      const returnTo = consumeReturnTo();
      if (returnTo) {
        window.history.replaceState(null, "", returnTo);
      }
      applyAuthResult(pca, result, "redirect");
    }
  } catch (error) {
    // Cancelled or failed hosted sign-in. The sign-in page offers "Try again" instead of redirecting
    // straight back, which could loop.
    console.error("Redirect sign-in failed", error);
    saveRedirectError(error);
  }

  const account = pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null;
  if (!account || store.getState().auth.accessToken || window.location.pathname === LOGGED_OUT_PATH) {
    return;
  }

  pca.setActiveAccount(account);
  try {
    applyAuthResult(pca, await pca.acquireTokenSilent({ scopes: loginRequest.scopes, account }));
  } catch (error) {
    // The session cannot be renewed silently. Drop the stale account (native sign-in refuses to
    // start while one is cached) and let the sign-in page take over, keeping the email as a hint.
    console.warn("Silent token renewal failed; sign-in is required", error);
    saveLoginHint(account.username);
    pca.setActiveAccount(null);
    await pca.clearCache({ account }).catch(() => undefined);
  }
}

function renderApp(pca: IPublicClientApplication) {
  const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

  root.render(
    <React.StrictMode>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <AffiliateProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <App pca={pca} />
            </LocalizationProvider>
          </AffiliateProvider>
        </PersistGate>
      </Provider>
    </React.StrictMode>
  );
}

function renderStartupFailure() {
  const root = document.getElementById("root");
  if (!root) return;
  const message = document.createElement("p");
  message.setAttribute("role", "alert");
  message.style.cssText = "font-family: sans-serif; text-align: center; margin-top: 20vh; padding: 0 16px;";
  message.textContent = "We couldn't start the app. Please refresh the page or try again later.";
  root.replaceChildren(message);
}

async function bootstrap() {
  // Started before MSAL rather than from a component effect, so branding is usually ready by the
  // time the first screen renders.
  void resolveAffiliateOnce(getAffiliateSlug()).catch(() => undefined);

  const pca = await createMsalInstance();
  msalInstance = pca;
  registerAuthEvents(pca);
  await restoreSession(pca);
  renderApp(pca);
}

bootstrap().catch((error) => {
  console.error("Application failed to start", error);
  renderStartupFailure();
});
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
